import { clsx } from 'clsx/lite';
import { SimInventoryActions, SimInventoryFilters, SimInventoryTable } from 'components';
import sharedPageStyles from 'pages/sharedPage.module.css';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import useOrganization from 'state/contexts/Organization/useOrganization';
import SimInventoryStoreProvider from 'state/SimInventory/SimInventoryStoreProvider';
import globalStyles from 'styles/shared.module.css';
import { SWRConfig, SWRConfiguration } from 'swr';
import type {
  ActivateButtonRenderer,
  OnSimsModified,
  OpenModalCallback,
  SimInventorySegmentName,
} from 'types/common';
import styles from './SimInventory.module.css';

interface SimInventoryProps {
  segmentName: SimInventorySegmentName | null;
  className?: string;
  simDetailBaseUrl: string;
  handleLinkClick: (url: string, value: any) => void;
  handleModalOpen: OpenModalCallback;
  activateButtonRenderer?: ActivateButtonRenderer;
}

const simInventorySwrConfig: SWRConfiguration = {
  dedupingInterval: 60000,
  focusThrottleInterval: 300000,
  loadingTimeout: 3000,
  errorRetryInterval: 10000,
  errorRetryCount: 3,
};

// TODO: (For lack of a better place to put this): Refactor legacy dashboard context into a useContext hook of some sort, that way we can swap out our own context when ready

const SimInventory = forwardRef<OnSimsModified, SimInventoryProps>(
  (
    {
      segmentName,
      className,
      simDetailBaseUrl,
      handleLinkClick,
      handleModalOpen,
      activateButtonRenderer,
    },
    ref
  ) => {
    const refreshRef = useRef<OnSimsModified>(null);
    const refreshFiltersRef = useRef<OnSimsModified>(null);
    const [validating, setValidating] = useState(false);

    const onSimsModified = useCallback<OnSimsModified>((simsModified?: Array<HologramSimId>) => {
      if (refreshRef.current) {
        refreshRef.current(simsModified);
      }

      if (refreshFiltersRef.current) {
        refreshFiltersRef.current(simsModified);
      }
    }, []);

    const onValidatingChange = useCallback((newValidating: boolean) => {
      setValidating(newValidating);
    }, []);

    useImperativeHandle(ref, () => onSimsModified, [onSimsModified]);

    let title;
    switch (segmentName) {
      case 'active':
        title = 'Active SIMs';
        break;
      case 'preflight':
        title = 'Pre-deployment SIMs';
        break;
      case 'deactivated':
        title = 'Deactivated SIMs';
        break;

      default:
        title = 'All SIMs';
        break;
    }

    const organizationId = useOrganization();

    if (!segmentName || (!organizationId && segmentName !== 'all')) {
      return null;
    }

    return (
      <SWRConfig value={simInventorySwrConfig}>
        <SimInventoryStoreProvider
          organizationId={organizationId}
          simInventorySegmentName={segmentName}
        >
          <Helmet>
            <title>{`${title} - Hologram`}</title>
          </Helmet>
          <div
            className={clsx(
              sharedPageStyles.pageWrapper,
              globalStyles.sharedLoadingAnimationParent,
              styles.wrapper,
              className
            )}
          >
            <div className={styles.leftSideContent}>
              <SimInventoryFilters ref={refreshFiltersRef} segmentName={segmentName} />
            </div>
            <div
              className={clsx(
                styles.rightSideContent,
                validating && globalStyles.sharedLoadingAnimation
              )}
            >
              <SimInventoryActions
                key={`actions-${segmentName}`}
                className={styles.actionBar}
                segmentName={segmentName}
                handleModalOpen={handleModalOpen}
                onSimsModified={onSimsModified}
                activateButtonRenderer={activateButtonRenderer}
              />
              <SimInventoryTable
                ref={refreshRef}
                key={`table-${segmentName}`}
                segmentName={segmentName}
                className={styles.table}
                simDetailBaseUrl={simDetailBaseUrl}
                handleLinkClick={handleLinkClick}
                onValidatingChange={onValidatingChange}
                activateButtonRenderer={activateButtonRenderer}
              />
            </div>
          </div>
        </SimInventoryStoreProvider>
      </SWRConfig>
    );
  }
);

export default SimInventory;
